/**
 * Nested comment-thread route (`.../comments/:id/thread`). Still a separate page from
 * `ClubPostThreadPage` + `CanonicalClubPostThreadShell` — same presentation tokens
 * (`feedPostCardPresentation`, `threadPresentation`) but different data entry and layout
 * shape; merging onto the canonical shell would be a follow-up if we want one code path.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Heart,
  Loader2,
  MessageCircle,
  Search,
  Share2,
} from "lucide-react";

import CommentThreadList from "../../features/comments/components/CommentThreadList.js";
import ThreadComposer from "../../features/comments/components/ThreadComposer.js";
import FeedPostCardHeader from "../../components/Clubs/FeedPostCardHeader";
import {
  addClubPostComment,
  buildClubPostShareUrl,
  fetchClub,
  fetchClubCommentThread,
  fetchClubPostById,
  fetchClubPostCommentsSearch,
  isLikelyFileOrBlob,
  toggleClubCommentLike,
  toggleClubPostLike,
} from "../../services/clubsApi";
import {
  bodyAfterFirstLine,
  formatClubCommentRelativeMeta,
  getOptimisticPostLikeState,
  getPostLikeStateFromToggleResponse,
  getImageAttachmentsFromPost,
  mergeClubCommentFromServer,
  mergeViewerOntoOwnClubCommentIfNeeded,
  messageForClubCommentLikeError,
  messageForClubCommentSubmitError,
  buildClubCommentSearchPreview,
  isClubCommentUserFacingSearchMatch,
  normalizeClubCommentRow,
  resolveClubMediaUrl,
  titleFromPost,
} from "../../features/comments/utils/clubFeedPostUtils.js";
import {
  CLUB_FEED_ACTION_SHADOW,
  clubFeedActionBase,
  clubFeedHeartClass,
  clubFeedNeutralButtonClass,
  clubPostCardActionRowClass,
  clubPostCardArticleClass,
  clubPostCardBodyClass,
  clubPostCardBodyTextClass,
  clubPostCardHeaderClass,
  clubPostCardTitleClass,
} from "../../components/Clubs/feedPostCardPresentation";
import {
  clubThreadCommentsStackClass,
  clubThreadCommentsWrapClass,
  clubThreadComposerWrapClass,
  clubThreadContinueThreadButtonClass,
  clubThreadSearchBtnClass,
  clubThreadSortMenuClass,
  clubThreadSortMenuItemClass,
  clubThreadSortTriggerClass,
  clubThreadToolsRowClass,
} from "../../features/comments/presentation/threadPresentation.js";
import { useAuth } from "../../context/AuthContext";
import { buildOptimisticLikeState, reconcileLikeStateFromResponse } from "../../utils/likeInteraction";
import ClubImageLightbox from "../../components/Clubs/ClubImageLightbox";
import ClubPostMediaGallery from "../../components/Clubs/ClubPostMediaGallery";

function cn(...xs) {
  return xs.filter(Boolean).join(" ");
}

function pickViewerId(user) {
  const keys = [
    user?.id,
    user?.user_id,
    user?.userId,
    user?.uid,
    user?.sub,
    user?.profile?.id,
    user?.profile?.user_id,
    user?.account?.id,
    user?.account?.user_id,
    user?.account_id,
  ];
  for (const v of keys) {
    if (v == null || v === "") continue;
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function isActiveMember(membership) {
  if (!membership) return false;
  return String(membership.status || "").toLowerCase() === "active";
}

function isModRole(role) {
  const r = String(role || "").toLowerCase();
  return ["owner", "moderator", "mod", "admin"].includes(r);
}

function canUserModerateClubComments({ viewerId, membership, club }) {
  if (viewerId == null) return false;
  const ownerId = club?.owner_user_id ?? club?.ownerUserId;
  if (ownerId != null && Number(ownerId) === Number(viewerId)) return true;
  return isModRole(membership?.role);
}

export default function ClubCommentThreadPage() {
  const { slug, postId: postIdParam, commentId: commentIdParam } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const viewerId = pickViewerId(user);

  const postId = useMemo(() => Number(postIdParam), [postIdParam]);
  const commentId = useMemo(() => Number(commentIdParam), [commentIdParam]);
  const fullDiscussionPath = `/clubs/${encodeURIComponent(slug || "")}/posts/${encodeURIComponent(
    String(postIdParam || "")
  )}`;

  const [comments, setComments] = useState([]);
  const [post, setPost] = useState(null);
  const [club, setClub] = useState(null);
  const [membership, setMembership] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [actionErr, setActionErr] = useState("");
  const [activeReplyTargetId, setActiveReplyTargetId] = useState(null);
  const [inlineReplyText, setInlineReplyText] = useState("");
  const [inlineSending, setInlineSending] = useState(false);
  const [likeBusy, setLikeBusy] = useState(false);
  const [shareHint, setShareHint] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const commentsSectionRef = useRef(null);
  const postMenuRef = useRef(null);
  const [postMenuOpen, setPostMenuOpen] = useState(false);
  const [highlightedCommentId, setHighlightedCommentId] = useState(null);
  const [pendingFocusCommentId, setPendingFocusCommentId] = useState(null);
  const [commentSort, setCommentSort] = useState("new");
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const sortMenuRef = useRef(null);
  const searchCommentsTriggerRef = useRef(null);
  const [searchMode, setSearchMode] = useState(false);
  const [commentSearchText, setCommentSearchText] = useState("");
  const [commentSearchRemote, setCommentSearchRemote] = useState({
    status: "idle",
    matches: [],
    errorMessage: "",
    searchTruncated: false,
    searchLimit: 0,
  });
  const [descendantsTruncated, setDescendantsTruncated] = useState(false);
  const [returnedDescendantCount, setReturnedDescendantCount] = useState(0);
  const commentSearchRequestEpochRef = useRef(0);

  const canParticipate = isActiveMember(membership);
  const canModerateComments = canUserModerateClubComments({ viewerId, membership, club });
  /** URL anchor comment (matched / continuation root id) within the loaded branch rows. */
  const anchor = useMemo(
    () => comments.find((c) => Number(c?.id) === commentId) ?? null,
    [comments, commentId]
  );
  const commentsForBranch = comments;

  const onCommentMutated = useCallback((row) => {
    const n = normalizeClubCommentRow(row);
    if (n == null || n.id == null) return;
    const idKey = String(n.id);
    const debugClubComments = process.env.REACT_APP_DEBUG_CLUB_COMMENTS === "1";
    if (debugClubComments) {
      // eslint-disable-next-line no-console
      console.info("[ClubCommentThreadPage onCommentMutated]", {
        idKey,
        is_deleted: n.is_deleted,
        contentLen: String(n.content ?? "").length,
        edited_at: n.edited_at,
      });
    }
    setComments((prev) => {
      let matched = false;
      const next = prev.map((c) => {
        if (String(c?.id) !== idKey) return c;
        matched = true;
        return mergeClubCommentFromServer(c, row);
      });
      if (debugClubComments) {
        // eslint-disable-next-line no-console
        console.info("[ClubCommentThreadPage onCommentMutated merge]", {
          idKey,
          matchedInList: matched,
          listLen: prev.length,
        });
      }
      return next;
    });
  }, []);

  const accent = useMemo(() => {
    const s = club?.settings && typeof club.settings === "object" ? club.settings : {};
    return s.accent === "sky" ? "sky" : "emerald";
  }, [club]);

  const loadThread = useCallback(async () => {
    if (!Number.isFinite(postId) || !Number.isFinite(commentId)) {
      setErr("Invalid comment link.");
      setDescendantsTruncated(false);
      setReturnedDescendantCount(0);
      setLoading(false);
      return;
    }
    setLoading(true);
    setErr("");
    try {
      const [threadRes, clubRes, postRes] = await Promise.all([
        fetchClubCommentThread(postId, commentId, { sort: commentSort }),
        slug ? fetchClub(slug).catch(() => null) : null,
        fetchClubPostById(postId),
      ]);
      const ancRaw = threadRes?.anchor ?? null;
      const descRaw = Array.isArray(threadRes?.comments) ? threadRes.comments : [];
      const anc = ancRaw ? normalizeClubCommentRow(ancRaw) : null;
      const desc = descRaw.map(normalizeClubCommentRow).filter(Boolean);
      // Branch-only: anchor + its descendant rows. Do not prepend API `parents` — that
      // duplicated the full ancestor chain and cramped the dedicated continuation view.
      setComments(anc ? [anc, ...desc] : []);
      setPost(postRes?.post ?? postRes ?? null);
      setClub(clubRes?.club ?? clubRes ?? null);
      setMembership(clubRes?.membership ?? null);
      setDescendantsTruncated(Boolean(threadRes?.descendants_truncated));
      setReturnedDescendantCount(Number(threadRes?.returned_descendant_count) || 0);
    } catch (e) {
      setErr(e?.message || "Could not load these replies.");
      setComments([]);
      setPost(null);
      setClub(null);
      setMembership(null);
      setDescendantsTruncated(false);
      setReturnedDescendantCount(0);
    } finally {
      setLoading(false);
    }
  }, [postId, commentId, slug, commentSort]);

  useEffect(() => {
    setActiveReplyTargetId(null);
    setInlineReplyText("");
    loadThread();
  }, [loadThread]);

  useEffect(() => {
    const focusId = Number(location.state?.focusCommentId);
    if (!Number.isFinite(focusId)) return;
    setPendingFocusCommentId(focusId);
  }, [location.key, location.state?.focusCommentId]);

  useEffect(() => {
    if (!sortMenuOpen) return;
    const onDoc = (e) => {
      if (sortMenuRef.current && !sortMenuRef.current.contains(e.target)) {
        setSortMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [sortMenuOpen]);

  useEffect(() => {
    if (!postMenuOpen) return;
    const onDoc = (e) => {
      if (postMenuRef.current && !postMenuRef.current.contains(e.target)) {
        setPostMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [postMenuOpen]);

  useEffect(() => {
    const effectivePostId = Number.isFinite(Number(post?.id)) ? Number(post.id) : postId;
    if (!searchMode || !Number.isFinite(effectivePostId)) {
      setCommentSearchRemote({
        status: "idle",
        matches: [],
        errorMessage: "",
        searchTruncated: false,
        searchLimit: 0,
      });
      return;
    }
    const q = String(commentSearchText || "").trim();
    if (!q) {
      setCommentSearchRemote({
        status: "idle",
        matches: [],
        errorMessage: "",
        searchTruncated: false,
        searchLimit: 0,
      });
      return;
    }

    const epoch = ++commentSearchRequestEpochRef.current;
    const ac = new AbortController();
    const timer = window.setTimeout(async () => {
      if (epoch !== commentSearchRequestEpochRef.current) return;
      setCommentSearchRemote((prev) => ({
        ...prev,
        status: "loading",
        errorMessage: "",
        searchTruncated: false,
      }));
      try {
        const data = await fetchClubPostCommentsSearch(effectivePostId, {
          q,
          limit: 35,
          signal: ac.signal,
        });
        if (epoch !== commentSearchRequestEpochRef.current) return;
        const list = Array.isArray(data?.matches) ? data.matches : [];
        const matches = list
          .map((row) => normalizeClubCommentRow(row))
          .filter(Boolean)
          .filter((row) => isClubCommentUserFacingSearchMatch(row, q));
        setCommentSearchRemote({
          status: "success",
          matches,
          errorMessage: "",
          searchTruncated: Boolean(data?.search_truncated),
          searchLimit: Number(data?.search_limit) || 35,
        });
      } catch (e) {
        if (e?.name === "AbortError") return;
        if (epoch !== commentSearchRequestEpochRef.current) return;
        const code = e?.data?.code;
        let msg = String(e?.message || "Could not search comments.");
        if (code === "CLUB_POST_NOT_FOUND") {
          msg =
            "This post was not found or is unavailable — search cannot run. Try refreshing the page.";
        } else if (code === "CLUB_POST_COMMENTS_FORBIDDEN") {
          msg = "Join this club to search comments on private posts.";
        }
        setCommentSearchRemote({
          status: "error",
          matches: [],
          errorMessage: msg,
        });
      }
    }, 260);

    return () => {
      window.clearTimeout(timer);
      ac.abort();
    };
  }, [searchMode, commentSearchText, post?.id, postId]);

  useEffect(() => {
    if (!commentsForBranch.length) return;
    const focusId = Number(pendingFocusCommentId);
    if (!Number.isFinite(focusId)) return;
    let attempts = 0;
    const maxAttempts = 16;
    let timer = null;
    const tryScroll = () => {
      const el = document.querySelector(`[data-comment-id="${focusId}"]`);
      if (!el) {
        attempts += 1;
        if (attempts < maxAttempts) {
          timer = window.setTimeout(tryScroll, 80);
        }
        return;
      }
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      setHighlightedCommentId(focusId);
      window.setTimeout(() => setHighlightedCommentId(null), 1800);
      setPendingFocusCommentId(null);
    };
    timer = window.setTimeout(tryScroll, 60);
    return () => {
      if (timer) window.clearTimeout(timer);
    };
  }, [commentsForBranch, pendingFocusCommentId]);

  const submitInlineReply = async (payload) => {
    const comment = payload?.comment ?? payload;
    const parentId = Number(comment?.id);
    const shouldGateDeepInline = Boolean(payload?.shouldGateDeepInline);
    const text = String(payload?.text ?? inlineReplyText ?? "").trim();
    const rawGif = payload?.gifUrl ?? null;
    const gifUrl =
      rawGif != null && String(rawGif).trim() !== "" ? String(rawGif).trim() : null;
    const rawFile = payload?.file ?? null;
    const file = isLikelyFileOrBlob(rawFile) ? rawFile : null;
    if ((!text && !gifUrl && !file) || !viewerId || !Number.isFinite(postId) || !canParticipate || inlineSending) return;
    if (!Number.isFinite(parentId)) return;
    try {
      setInlineSending(true);
      setActionErr("");
      const created = await addClubPostComment(postId, text, {
        parentCommentId: parentId,
        gifUrl,
        file,
      });
      setInlineReplyText("");
      setActiveReplyTargetId(null);
      if (shouldGateDeepInline) {
        navigate(
          `/clubs/${encodeURIComponent(String(slug || ""))}/posts/${encodeURIComponent(
            String(postId)
          )}/comments/${encodeURIComponent(String(parentId))}/thread`,
          { state: { focusCommentId: created?.id } }
        );
        return;
      }
      const normalized = mergeViewerOntoOwnClubCommentIfNeeded(
        normalizeClubCommentRow(created),
        viewerId,
        user ? { username: user.username, profile_picture: user.profile_picture } : null
      );
      setComments((prev) => [...prev, normalized]);
      setPost((prev) => {
        if (!prev) return prev;
        const n = (Number(prev.comment_count) || 0) + 1;
        return { ...prev, comment_count: n };
      });
      const newId = Number(created?.id);
      if (Number.isFinite(newId)) {
        setPendingFocusCommentId(newId);
      }
    } catch (e) {
      setActionErr(messageForClubCommentSubmitError(e, "Could not post reply."));
      throw e;
    } finally {
      setInlineSending(false);
    }
  };

  const openCommentSearchHit = useCallback(
    (hit) => {
      const cid = Number(hit?.id);
      const pid = Number.isFinite(Number(post?.id)) ? Number(post.id) : postId;
      if (!Number.isFinite(cid) || !Number.isFinite(pid) || !slug) return;
      navigate(
        `/clubs/${encodeURIComponent(String(slug))}/posts/${encodeURIComponent(
          String(pid)
        )}/comments/${encodeURIComponent(String(cid))}/thread`,
        { state: { focusCommentId: cid } }
      );
    },
    [navigate, post?.id, postId, slug]
  );

  const onToggleLike = async () => {
    if (!viewerId || !post?.id || !canParticipate) return;
    const optimistic = getOptimisticPostLikeState({ liked, likeCount });
    try {
      setLikeBusy(true);
      setActionErr("");
      setPost((prev) =>
        prev
          ? {
              ...prev,
              liked_by_viewer: optimistic.optimisticLiked,
              like_count: optimistic.optimisticCount,
            }
          : prev
      );
      const res = await toggleClubPostLike(post.id);
      const { nextLiked, nextCount } = getPostLikeStateFromToggleResponse(res, optimistic);
      setPost((prev) =>
        prev
          ? {
              ...prev,
              liked_by_viewer: nextLiked,
              like_count: nextCount,
            }
          : prev
      );
    } catch (e) {
      setPost((prev) =>
        prev
          ? {
              ...prev,
              liked_by_viewer: optimistic.prevLiked,
              like_count: optimistic.prevCount,
            }
          : prev
      );
      setActionErr(e?.message || "Could not update like.");
    } finally {
      setLikeBusy(false);
    }
  };

  const onToggleCommentLike = async (comment) => {
    const commentIdNum = Number(comment?.id);
    if (!viewerId || !Number.isFinite(postId) || !canParticipate || !Number.isFinite(commentIdNum)) return;
    const sourceRow = comments.find((row) => Number(row?.id) === commentIdNum);
    const optimisticState = buildOptimisticLikeState({
      liked_by_viewer: sourceRow?.liked_by_viewer,
      like_count: sourceRow?.like_count,
    });

    setComments((prev) =>
      prev.map((row) =>
        Number(row?.id) === commentIdNum
          ? {
              ...row,
              liked_by_viewer: optimisticState.optimisticLiked,
              like_count: optimisticState.optimisticCount,
            }
          : row
      )
    );
    try {
    const res = await toggleClubCommentLike(postId, commentIdNum);
    const canonical = reconcileLikeStateFromResponse(res, optimisticState);
    setComments((prev) =>
      prev.map((row) => {
        if (Number(row?.id) !== commentIdNum) return row;
        return {
          ...row,
          liked_by_viewer: canonical.liked_by_viewer,
          like_count: canonical.like_count,
        };
      })
    );
    } catch (e) {
      setComments((prev) =>
        prev.map((row) =>
          Number(row?.id) === commentIdNum
            ? {
                ...row,
                liked_by_viewer: optimisticState.prevLiked,
                like_count: optimisticState.prevCount,
              }
            : row
        )
      );
      setActionErr(messageForClubCommentLikeError(e, "Could not update comment like."));
    }
  };

  const onShare = async () => {
    const url = buildClubPostShareUrl(slug, post?.id);
    if (!url) return;
    if (navigator.share) {
      try {
        await navigator.share({
          title: club?.name ? `${club.name} — replies` : "Replies",
          text: "Check out these replies on AnimeHouse",
          url,
        });
        setShareHint("Shared.");
        setTimeout(() => setShareHint(""), 2500);
        return;
      } catch (e) {
        if (e?.name === "AbortError") return;
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setShareHint("Link copied.");
      setTimeout(() => setShareHint(""), 2500);
    } catch {
      prompt("Copy this link:", url);
    }
  };

  const scrollToComments = useCallback(() => {
    const el = commentsSectionRef.current;
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - 100;
    window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
  }, []);

  const imageAttachments = useMemo(() => getImageAttachmentsFromPost(post), [post]);
  const attachmentUrls = useMemo(
    () => imageAttachments.map((a) => resolveClubMediaUrl(a.url)).filter(Boolean),
    [imageAttachments]
  );
  const legacyImageUrl = resolveClubMediaUrl(post?.image_url);
  const previewImages = useMemo(() => {
    if (attachmentUrls.length) return attachmentUrls;
    return legacyImageUrl ? [legacyImageUrl] : [];
  }, [attachmentUrls, legacyImageUrl]);
  const postHeading = titleFromPost(post);
  const postBodyRest = bodyAfterFirstLine(post);
  const likeCount = Number(post?.like_count) || 0;
  const liked = Boolean(post?.liked_by_viewer);
  const commentCount = Number(post?.comment_count) || commentsForBranch.length;
  const feedHeartTokens = clubFeedHeartClass(accent, liked);
  const feedNeutralAction = clubFeedNeutralButtonClass();

  return (
    <div className="min-h-[60vh] w-full text-white px-3 sm:px-5 pt-5 pb-16">
      <div className="mx-auto w-full max-w-[1200px]">
        <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-3 sm:p-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 rounded-xl border border-white/12 bg-white/8 px-3 py-2 text-sm text-gray-100 hover:bg-white/12 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>
            <p className="text-sm sm:text-base font-semibold text-gray-200 truncate">Replies</p>
          </div>
          <Link
            to={fullDiscussionPath}
            className="text-xs sm:text-sm text-emerald-200 hover:text-emerald-100 transition-colors whitespace-nowrap"
          >
            See full discussion
          </Link>
        </div>

        {actionErr ? (
          <div className="mt-3 rounded-xl border border-red-500/25 bg-red-500/10 px-4 py-2 text-sm text-red-200">
            {actionErr}
          </div>
        ) : null}

        {loading ? (
          <div className="mt-4 flex items-center gap-2 text-sm text-gray-400">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading replies…
          </div>
        ) : err ? (
          <div className="mt-4 rounded-xl border border-red-500/25 bg-red-500/10 px-4 py-3 text-sm text-red-200">
            {err}
          </div>
        ) : !post || !anchor ? (
          <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-gray-400">
            These replies aren’t available.
          </div>
        ) : (
          <>
            <article aria-label="Original club post" className={clubPostCardArticleClass}>
              <div className={clubPostCardHeaderClass}>
                <FeedPostCardHeader
                  post={post}
                  club={club}
                  clubSlug={String(slug || "").trim()}
                  variant="thread"
                  menuOpen={postMenuOpen}
                  setMenuOpen={setPostMenuOpen}
                  menuRef={postMenuRef}
                  onShare={(e) => {
                    e?.stopPropagation?.();
                    onShare();
                  }}
                  canDelete={false}
                />
              </div>
              <div className={clubPostCardBodyClass}>
                <h1 className={clubPostCardTitleClass}>{postHeading}</h1>
                {postBodyRest ? <div className={clubPostCardBodyTextClass + " mt-4"}>{postBodyRest}</div> : null}
                {previewImages.length ? (
                  <ClubPostMediaGallery
                    className="mt-6"
                    images={previewImages}
                    variant="thread"
                    onOpenAt={(idx) => {
                      setPreviewIndex(idx);
                      setPreviewOpen(true);
                    }}
                  />
                ) : null}
              </div>
              <div className={clubPostCardActionRowClass}>
                {shareHint ? <p className="mb-2 text-xs text-emerald-300/90">{shareHint}</p> : null}
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={onToggleLike}
                    disabled={likeBusy || !viewerId || !canParticipate}
                    aria-pressed={liked}
                    aria-label={
                      liked ? `Unlike, ${likeCount} likes` : `Like, currently ${likeCount} likes`
                    }
                    title={
                      !viewerId
                        ? "Log in to like"
                        : !canParticipate
                          ? "Join this club to like"
                          : liked
                            ? "Unlike"
                            : "Like"
                    }
                    className={cn(
                      clubFeedActionBase,
                      feedHeartTokens,
                      CLUB_FEED_ACTION_SHADOW,
                      !viewerId || !canParticipate ? "opacity-50 cursor-not-allowed" : ""
                    )}
                  >
                    <Heart
                      className={
                        liked
                          ? "h-3.5 w-3.5 fill-current text-emerald-300"
                          : "h-3.5 w-3.5 text-white/70"
                      }
                    />
                    <span className="tabular-nums">{likeCount}</span>
                  </button>
                  <button type="button" onClick={scrollToComments} className={feedNeutralAction}>
                    <MessageCircle className="h-3.5 w-3.5" />
                    <span>{commentCount}</span>
                  </button>
                  <button type="button" onClick={onShare} className={feedNeutralAction}>
                    <Share2 className="h-3.5 w-3.5" />
                    Share
                  </button>
                </div>
              </div>
            </article>

            <div
              ref={commentsSectionRef}
              className="mt-4 rounded-2xl border border-white/5 bg-transparent px-3 sm:px-5 md:px-6 py-5 sm:py-6"
            >
              <p className="text-sm sm:text-base font-semibold text-gray-200 pb-3 border-b border-white/4">
                Replies
              </p>
              {searchMode ? (
                <div className={clubThreadComposerWrapClass}>
                  <ThreadComposer
                    viewerId={viewerId}
                    canParticipate={canParticipate}
                    replyText=""
                    sending={false}
                    onReplyTextChange={() => {}}
                    onSubmit={async () => {}}
                    isSearchMode
                    searchText={commentSearchText}
                    onSearchTextChange={setCommentSearchText}
                    onExitSearchMode={() => {
                      setSearchMode(false);
                      setCommentSearchText("");
                      setCommentSearchRemote({
                        status: "idle",
                        matches: [],
                        errorMessage: "",
                        searchTruncated: false,
                        searchLimit: 0,
                      });
                    }}
                    searchFocusReturnRef={searchCommentsTriggerRef}
                    showCancelButton
                    onCancel={() => {
                      setSearchMode(false);
                      setCommentSearchText("");
                      setCommentSearchRemote({
                        status: "idle",
                        matches: [],
                        errorMessage: "",
                        searchTruncated: false,
                        searchLimit: 0,
                      });
                    }}
                    className="px-0 py-0 border-0"
                  />
                </div>
              ) : null}
              <div className={clubThreadToolsRowClass}>
                <div className="relative" ref={sortMenuRef}>
                  <button
                    type="button"
                    className={clubThreadSortTriggerClass}
                    onClick={() => setSortMenuOpen((v) => !v)}
                  >
                    Sort by
                    <span className="font-semibold capitalize text-gray-100">{commentSort}</span>
                    <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
                  </button>
                  {sortMenuOpen ? (
                    <div className={clubThreadSortMenuClass}>
                      {[
                        { id: "new", label: "New" },
                        { id: "old", label: "Old" },
                        { id: "top", label: "Top" },
                      ].map((opt) => (
                        <button
                          key={opt.id}
                          type="button"
                          className={clubThreadSortMenuItemClass(commentSort === opt.id)}
                          onClick={() => {
                            setCommentSort(opt.id);
                            setSortMenuOpen(false);
                          }}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </div>
                <div className="flex flex-wrap items-center gap-3 justify-end sm:justify-start">
                  <button
                    ref={searchCommentsTriggerRef}
                    type="button"
                    className={clubThreadSearchBtnClass}
                    aria-expanded={searchMode}
                    onClick={() => {
                      setSearchMode(true);
                      setSortMenuOpen(false);
                    }}
                  >
                    <Search className="h-3.5 w-3.5 shrink-0" aria-hidden />
                    Search Comments
                  </button>
                  <Link
                    to={fullDiscussionPath}
                    className="text-xs sm:text-sm text-emerald-200 hover:text-emerald-100 transition-colors whitespace-nowrap"
                  >
                    See full discussion
                  </Link>
                </div>
              </div>
              {descendantsTruncated ? (
                <div
                  className="mt-3 rounded-lg border border-white/8 bg-white/[0.03] px-3 py-2.5 text-xs sm:text-sm text-gray-500 leading-relaxed"
                  role="status"
                >
                  Showing replies.{" "}
                  <Link
                    to={fullDiscussionPath}
                    className="text-emerald-200/90 hover:text-emerald-100 underline-offset-2 hover:underline"
                  >
                    Open the full discussion
                  </Link>{" "}
                  to view all replies.
                </div>
              ) : null}
              <div className={[clubThreadCommentsWrapClass, "pt-5 sm:pt-6"].join(" ")}>
                <div className={clubThreadCommentsStackClass}>
                  {searchMode ? (
                    <div
                      data-club-full-comment-search
                      className="rounded-xl border border-white/10 bg-white/[0.03] px-3.5 py-3 sm:px-4 sm:py-3.5 mb-3 sm:mb-3.5"
                      role="region"
                      aria-label="Full discussion comment search results"
                    >
                      <p className="text-[11px] sm:text-xs font-medium text-gray-400 mb-2">
                        Search all comments
                        <span className="text-gray-500 font-normal">
                          {" "}
                          (all comments on this post)
                        </span>
                      </p>
                      {!String(commentSearchText || "").trim() ? (
                        <p className="text-xs sm:text-sm text-gray-500 leading-relaxed">
                          Type a keyword to search every comment on this post, including replies
                          shown when you tap "View more replies."
                        </p>
                      ) : commentSearchRemote.status === "loading" ? (
                        <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-400">
                          <Loader2 className="h-4 w-4 animate-spin shrink-0" aria-hidden />
                          Searching…
                        </div>
                      ) : commentSearchRemote.status === "error" ? (
                        <p className="text-xs sm:text-sm text-amber-200/90">
                          {commentSearchRemote.errorMessage || "Search failed."}
                        </p>
                      ) : commentSearchRemote.matches.length === 0 ? (
                        <p className="text-xs sm:text-sm text-gray-500" role="status">
                          No comments match that search in the full discussion.
                        </p>
                      ) : (
                        <>
                          <ul className="space-y-2 max-h-[min(360px,50vh)] overflow-y-auto pr-0.5">
                            {commentSearchRemote.matches.map((hit) => {
                              const preview = buildClubCommentSearchPreview(hit, commentSearchText, 168);
                              const excerpt = preview.excerpt || "No visible comment text.";
                              const uname = hit?.username || "Member";
                              const hitTime = formatClubCommentRelativeMeta(hit.created_at);
                              const pid = hit?.parent_comment_id;
                              const ctx =
                                pid != null && Number.isFinite(Number(pid))
                                  ? "Nested reply"
                                  : "Top-level";
                              return (
                                <li
                                  key={String(hit.id)}
                                  className="rounded-lg border border-white/[0.06] bg-black/20 px-3 py-2.5"
                                >
                                  <div className="flex flex-wrap items-start justify-between gap-2">
                                    <div className="min-w-0 flex-1">
                                      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                                        <span className="text-xs sm:text-sm font-semibold text-gray-100">
                                          {uname}
                                        </span>
                                        <time
                                          dateTime={hitTime.dateTimeAttr || undefined}
                                          className="text-[10px] sm:text-[11px] text-gray-500 tabular-nums"
                                          title={hitTime.title || undefined}
                                        >
                                          {hitTime.label || hitTime.title || "—"}
                                        </time>
                                        <span className="text-[10px] sm:text-[11px] text-gray-500">
                                          · {ctx}
                                        </span>
                                      </div>
                                      <p className="mt-1 text-xs sm:text-sm text-gray-300/90 leading-relaxed break-words">
                                        {preview.highlightedExcerpt ? (
                                          <>
                                            {preview.highlightedExcerpt.before}
                                            <mark className="rounded bg-emerald-500/25 px-0.5 text-emerald-100">
                                              {preview.highlightedExcerpt.match}
                                            </mark>
                                            {preview.highlightedExcerpt.after}
                                          </>
                                        ) : (
                                          excerpt
                                        )}
                                      </p>
                                      {preview.matchedIn === "username" ? (
                                        <p className="mt-1 text-[10px] sm:text-[11px] text-gray-500">
                                          Matched in username. Preview shows this comment.
                                        </p>
                                      ) : null}
                                      {preview.matchedIn === "raw" ? (
                                        <p className="mt-1 text-[10px] sm:text-[11px] text-gray-500">
                                          Matched in attached content. Preview shows this comment.
                                        </p>
                                      ) : null}
                                    </div>
                                    <button
                                      type="button"
                                      onMouseDown={(e) => e.preventDefault()}
                                      onClick={() => openCommentSearchHit(hit)}
                                      className={cn(
                                        clubThreadContinueThreadButtonClass,
                                        "shrink-0 gap-1"
                                      )}
                                      aria-label={`Open replies for comment ${hit.id} by ${uname}`}
                                    >
                                      Open
                                      <ChevronRight className="h-3.5 w-3.5 opacity-90" aria-hidden />
                                    </button>
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                          {commentSearchRemote.searchTruncated ? (
                            <p
                              className="mt-2 pt-2 border-t border-white/[0.06] text-[10px] sm:text-[11px] text-gray-500 leading-relaxed"
                              role="status"
                            >
                              Showing the first {commentSearchRemote.matches.length} matches. More may
                              exist—try a more specific keyword.
                            </p>
                          ) : null}
                        </>
                      )}
                    </div>
                  ) : null}
                  <CommentThreadList
                    comments={commentsForBranch}
                    rootCommentSort={commentSort}
                    searchActive={
                      searchMode &&
                      !!String(commentSearchText || "").trim() &&
                      !(
                        commentSearchRemote.status === "success" &&
                        commentSearchRemote.matches.length > 0
                      )
                    }
                    searchQuery={commentSearchText}
                    viewerId={viewerId}
                    canParticipate={canParticipate}
                    activeReplyTargetId={activeReplyTargetId}
                    setActiveReplyTargetId={setActiveReplyTargetId}
                    inlineReplyText={inlineReplyText}
                    setInlineReplyText={setInlineReplyText}
                    inlineSending={inlineSending}
                    onSubmitInlineReply={submitInlineReply}
                    highlightedCommentId={highlightedCommentId}
                    focusCommentId={pendingFocusCommentId}
                    slug={slug}
                    postId={post?.id ?? postIdParam}
                    onToggleCommentLike={onToggleCommentLike}
                    canModerateComments={canModerateComments}
                    onCommentMutated={onCommentMutated}
                    onCommentActionError={(m) => setActionErr(m || "")}
                    onContinueThread={(nextCommentId, opts) => {
                      if (!slug || !postIdParam || !nextCommentId) return;
                      const rawFocus = opts?.focusCommentId;
                      const focusNum = Number(rawFocus);
                      const state = Number.isFinite(focusNum)
                        ? { focusCommentId: focusNum }
                        : undefined;
                      navigate(
                        `/clubs/${encodeURIComponent(String(slug))}/posts/${encodeURIComponent(
                          String(postIdParam)
                        )}/comments/${encodeURIComponent(String(nextCommentId))}/thread`,
                        state ? { state } : undefined
                      );
                    }}
                  />
                </div>
              </div>
            </div>
          </>
        )}

        <ClubImageLightbox
          isOpen={previewOpen}
          images={previewImages}
          currentIndex={previewIndex}
          onClose={() => {
            setPreviewOpen(false);
            setPreviewIndex(0);
          }}
          onIndexChange={setPreviewIndex}
        />
      </div>
    </div>
  );
}
